import { persist, subscribeWithSelector } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import type { StateCreator } from 'zustand/vanilla';

import { createDevtools } from '../middleware/create-dev-tools';
import { initialState, type WorkspaceState } from './initial-state';
import { createCurrentSlice, type WorkspaceCurrentAction } from './slices/current/action';
import { createPrivateSlice, type WorkspacePrivateAction } from './slices/private/action';

//  ===============  Aggregate type ============ //

export type WorkspaceStore = WorkspaceState & WorkspaceCurrentAction & WorkspacePrivateAction;

//  ===============  Aggregate createStoreFn ============ //

const createStore: StateCreator<WorkspaceStore, [['zustand/persist', unknown], ['zustand/devtools', never]]> = (...parameters) => ({
  ...initialState,
  ...createCurrentSlice(...parameters),
  ...createPrivateSlice(...parameters),
});

//  ===============  Implement useStore ============ //

const devtools = createDevtools('workspace');

export const useWorkspaceStore = createWithEqualityFn<WorkspaceStore>()(
  subscribeWithSelector(
    persist(devtools(createStore), {
      name: 'rubidex-workspace-storage',
      partialize: (state) => ({
        currentSpace: state.currentSpace,
        currentSpaceId: state.currentSpaceId,
        privateSpace: state.privateSpace,
      }),
    }),
  ),
  shallow,
);

export const getWorkspaceStoreState = () => useWorkspaceStore.getState();
